import { TextField } from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useHotkeys } from 'react-hotkeys-hook';

import { Button } from '@/components/Button';
import {
  formatIsoDuration,
  formatMinutes,
  parseDurationText,
  toIsoDuration,
} from '@/tracker/entities/worklog';
import { CreateTimelogRequest, Timelog } from '@/tracker/types';
import { isMac } from '@/utils/isMac';
import { parseDuration } from '@/utils/parseDuration';
import { parseTimeExpression } from '@/utils/parseTimeInput';

import s from './WorklogForm.module.scss';

type FormValues = { start: string; duration: string; remaining: string; comment: string };

export type WorklogFormResult = {
  worklog: Omit<CreateTimelogRequest, 'task'>;
  /** Новый остаток оценки. undefined — поле пустое, остаток не меняем */
  estimation?: string;
};

type Props = {
  /** Текущий остаток оценки задачи */
  estimation: string | null;
  /** Правка записи: поля заполнены ею, остаток оценки не трогаем */
  worklog?: Timelog;
  disabled: boolean;
  onSubmit: (data: WorklogFormResult) => void;
  onCancel: () => void;
};

const LOCAL_DATETIME = "yyyy-MM-dd'T'HH:mm";

const WorklogForm: React.FC<Props> = ({ estimation, worklog, disabled, onSubmit, onCancel }) => {
  const estimationMinutes = estimation === null ? null : parseDuration(estimation);
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    getFieldState,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      start: (worklog ? DateTime.fromISO(worklog.start) : DateTime.now()).toFormat(LOCAL_DATETIME),
      duration: worklog ? formatIsoDuration(worklog.duration) : '',
      remaining: estimationMinutes === null ? '' : formatMinutes(estimationMinutes),
      comment: worklog?.comment ?? '',
    },
  });
  const duration = watch('duration');
  const durationMinutes = parseTimeExpression(duration);

  // Как в Яндекс Трекере: остаток уменьшается на введённое время, пока его не поправили руками
  useEffect(() => {
    if (worklog || estimationMinutes === null || getFieldState('remaining').isDirty) return;

    const spent = parseTimeExpression(duration) ?? 0;

    setValue('remaining', formatMinutes(Math.max(0, estimationMinutes - spent)));
  }, [worklog, duration, estimationMinutes, getFieldState, setValue]);

  const submit = handleSubmit(({ start, duration, remaining, comment }) => {
    const minutes = parseTimeExpression(duration);
    const remainingMinutes = remaining.trim() ? parseDurationText(remaining) : null;

    if (!minutes) {
      setError('duration', { message: 'Неверный формат времени' });

      return;
    }

    if (remaining.trim() && remainingMinutes === null) {
      setError('remaining', { message: 'Неверный формат времени' });

      return;
    }

    onSubmit({
      worklog: {
        start: DateTime.fromFormat(start, LOCAL_DATETIME).toISO()!,
        duration: toIsoDuration(minutes),
        comment: comment.trim(),
      },
      estimation: remainingMinutes === null ? undefined : toIsoDuration(remainingMinutes),
    });
  });

  useHotkeys('mod+enter', () => submit(), { enableOnFormTags: true });

  return (
    <form className={s.form} onSubmit={submit}>
      <div className={s.form__row}>
        <TextField
          type="datetime-local"
          label="Начало работы"
          size="small"
          {...register('start', { required: true })}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Затрачено"
          size="small"
          placeholder="1ч 30м"
          autoFocus
          autoComplete="off"
          error={!!errors.duration}
          // Как понято введённое: «90м» → «1ч 30м»
          helperText={
            errors.duration?.message ??
            (durationMinutes ? `= ${formatMinutes(durationMinutes)}` : ' ')
          }
          {...register('duration', { required: 'Укажите время' })}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 160 }}
        />
        {!worklog && (
          <TextField
            label="Осталось"
            size="small"
            placeholder="0ч"
            error={!!errors.remaining}
            helperText={errors.remaining?.message}
            {...register('remaining')}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: 140 }}
          />
        )}
      </div>
      <TextField label="Комментарий" size="small" multiline minRows={2} {...register('comment')} />
      <div className={s.form__actions}>
        <Button type="submit" disabled={disabled}>
          {worklog ? 'Сохранить' : 'Записать'}
        </Button>
        <Button variant="text" onClick={onCancel}>
          Отмена
        </Button>
        <span className={s.form__hint}>{isMac ? '⌘' : 'Ctrl'} + Enter</span>
      </div>
    </form>
  );
};

export { WorklogForm };
